import * as errors from '../errors';
import InputError from '../core/InputError';

/**
 * Operations on a manifest of an application.
 */
const randSize = 1000000;

/**
 * Get a random id
 *  @returns {string} The id.
 */
export const getRandomizedIdString = function() {
    return `id-${(Math.random() * randSize).toFixed(0)}`;
};


/**
 * Visits each node and calls the callback on that node.
 *  @param {*} node The manifest to define the node's properties.
 *  @param {*} parentNode The node that is parent of the current node.
 *  @param {function} callback The function to call on each visited node.
 *  @returns {void}
 */
export const visit = function(node, parentNode, callback) {
    if (!node) {
        return;
    }

    callback(node, parentNode);

    if (node && node.children && node.children.length !== 0) {
        for (let i = 0; i <= node.children.length; i++) {
            visit(node.children[i], node, callback);
        }
    }
};

export const setFullValidDataOnManifest = function(manifest) {
    visit(manifest, null, (node) => {
        // eslint-disable-next-line no-use-before-define
        let valueObject = getValidValueObject(node.element, node.options);
        if (valueObject) {
            node.values = valueObject;
        }
    });
};

/**
 * Gets a node from the schema manifest by id
 *  @param {*} node The manifest to define the node's properties.
 *  @param {string} id The id of the node to find.
 *  @returns {*} The found node.
 */
export const getNodeByIdRecursive = function(node, id) {
    let foundNode;
    if (node && node.attributes && node.attributes.id === id) {
        return node;
    }

    if (node && node.children && node.children.length !== 0) {
        for (let i = 0; i <= node.children.length; i++) {
            foundNode = getNodeByIdRecursive(node.children[i], id);
            if (foundNode) {
                return foundNode;
            }
        }
    }

    return null;
};

/**
 * Gets a node from the schema manifest by id
 *  @param {*} manifest The manifest to define the node's properties.
 *  @param {string} id The id of the node to find.
 *  @returns {*} The found node.
 */
export const getNodeById = function(manifest, id) {
    return getNodeByIdRecursive(manifest, id);
};

/**
 * Gets an array of nodes from the schema manifest by element type
 *  @param {*} manifest The manifest to define the node's properties.
 *  @param {string} elementType The elementType of the node to find.
 *  @param {string} excludedByParentType The elementType of the
 *  parent node to to exclude.
 *  @returns {Array} The found nodes.
 */
export const getNodesByElementType = function(manifest, elementType, excludedByParentType) {
    let nodes = [];
    visit(manifest, null, (node, parentNode) => {
        if (parentNode
            && excludedByParentType
            && parentNode.element === excludedByParentType) {
            return;
        }

        if (node.element === elementType) {
            nodes.push(node);
        }
    });

    return nodes;
};

export const getNearestAncestorByTagName = function(manifest, id, tagName) {
    let node = getNodeById(manifest, id);
    while (node.parentNode) {
        node = node.parentNode;
        if (node.element === tagName) {
            return node;
        }
    }

    return null;
};

/**
 * Takes an inline error node created by the server and formats it
 * be consumed by the client framework.
 *  @param {*} node The error node.
 *  @param {*} parentNode The error node's parent.
 *  @returns {void}
 */
const decorateServerErrorNode = (node, parentNode) => {
    // API puts the error on the "text" property
    let message = node.errors[0].text || 'Error On Input Control From Server.';
    let options = {
        validationObject: InputError.createValidationObject(message),
        validationMessage: message,
    };

    options.id = node.errors[0].id;
    options.shortName = options.id.charAt(0).toUpperCase() + options.id.slice(1);

    // this an error on the child of a complex input
    if (node.errors[0].id !== parentNode.attributes.id) {
        options.parentId = parentNode.attributes.id;
        options.parentShortName = parentNode.attribute.shortName
            || options.parentId.charAt(0).toUpperCase() + options.parentId.slice(1);
    }

    let inputError = new InputError(options);
    node.errors[0].localText = inputError.localMessage;
    node.errors[0].globalText = inputError.globalMessage;
};

/**
 * Traverses the full manifest and standardizes it as needed.
 *  @param {*} manifest The full manifest.
 *  @returns {void}
 */
export const standardize = function(manifest) {
    // if (!this.getGlobalErrorContainer(manifest)) {
    //     setFullValidDataOnManifest(manifest);
    // }

    visit(manifest, null, (node, parentNode) => {
        if (node) {
            node.parentNode = parentNode;

            // add required properties
            if (!node.attributes) {
                node.attributes = {};
            }

            if (!node.children) {
                node.children = [];
            }

            // this needs to exist
            if (!node.values) {
                node.values = [];
            }

            // if a node doesn't have an id it is not an input
            if (node.attributes.id) {
                // Each input component will watch this property
                // when changed, it will raise its update error event
                node.shouldUpdateError = null;
            }

            // preprocess
            if (node.attributes
                && node.attributes.required) {
                node.attributes.required = true;
            }

            if (node.element === 'lni-tab-panel') {
                if (node.attributes.isDefaultTab === 'true') {
                    node.attributes.showTab = 'true';
                } else {
                    node.attributes.showTab = 'false';
                }
            }

            if (node.attributes.id === 'app-container') {
                let errorContainerNode
                    = node.children.find((e) => e.element === 'x-error-container');
                if (!errorContainerNode) {
                    // eslint-disable-next-line no-use-before-define
                    errorContainerNode = getEmptyGlobalErrorNode();
                    errorContainerNode.errors.push({
                        id: 'none',
                        text: 'BeforeTouch',
                    });
                    node.children.unshift(errorContainerNode);
                }
            }

            // standardize the inline error inputs added by the API
            if (node.element === 'x-error') {
                if (node.errors && Array.isArray(node.errors)) {
                    if (node.errors.length !== 1) {
                        throw new errors.InvalidManifestError(
                            node.element,
                            node.attributes.id,
                            'Errors array should only have 1 element.');
                    }

                    decorateServerErrorNode(node, parentNode);
                }
            }

            // add global defaults
            // These would, in future, be added by the back end.
            if (node.element === 'x-input-address') {
                if (node.values.length === 0) {
                    node.values = [{
                        state: 'WA',
                    },
                    ];
                }
            }
        }
    });
};

/**
 * Traverses the full manifest and standardizes it as needed.
 *  @param {*} manifest The full manifest.
 *  @returns {void}
 */
export const standardizeForSubmit = function(manifest) {
    // eslint-disable-next-line
    visit(manifest, null, (node) => {
        delete node.parentNode;
    });
};


/**
 * Takes a nodeManifest and generates a unique id
 *  @param {*} nodeManifest The manifest to define the node's properties.
 *  @returns {string} A unique id.
 */
export const slugifyFormID = function(nodeManifest) {
    // Try to get a reasonable default id from the schema,
    // then slugify it.
    if (typeof nodeManifest.attributes.id !== 'undefined') {
        // If an ID's been explicitly set, use it unchanged
        return nodeManifest.attributes.id;
    }
    // Return the slugified version of either:
    let val = nodeManifest.attributes.label || nodeManifest.attributes.model;

    if (val === undefined) {
        val = 'placeholder';
    }
    return (
        val
            // avoiding extra dependencies.
            .toString()
            .trim()
            .toLowerCase()
            // Spaces & underscores to dashes
            .replace(/ |_/g, '-')
            // Multiple dashes to one
            .replace(/-{2,}/g, '-')
            // Remove leading & trailing dashes
            .replace(/^-+|-+$/g, '')
            // Remove anything that isn't a (English/ASCII) letter, number or dash.
            .replace(/([^a-zA-Z0-9-]+)/g, '')
    );
};

/**
 * Gets the guid from the schema manifest
 *  @param {*} manifest The manifest to define the node's properties.
 *  @returns {string } The guid of form or null.
 */
export const getFormGuid = (manifest) => {
    let node = getNodeById(manifest, 'guid_value');
    if (node) {
        return node.values[0].value;
    }

    return '';
};

/**
 * Utility function to determine whether a node has a value
 *  @param {*} node The node to check.
 *  @returns {boolean} whether it has a value.
 */
// eslint-disable-next-line arrow-body-style
export const nodeHasValue = (node) => {
    return node
        && node.values
        && Array.isArray(node.values)
        && node.values.length > 0;
};

// eslint-disable-next-line arrow-body-style
const getErrorNode = (errorMessageObject, controlId, parentId) => {
    let globalText = errorMessageObject ? errorMessageObject.globalErrorMessage : '';
    return {
        element: 'x-error',
        attributes: {},
        errors: [
            {
                value: '',
                localText: errorMessageObject ? errorMessageObject.localErrorMessage : '',
                globalText,
                id: controlId,
                parentId,
            },
        ],
    };
};

// eslint-disable-next-line arrow-body-style
const getErrorNodeFromExistingNode = (errorNode, global) => {
    return {
        element: 'x-error',
        attributes: {
            global,
        },
        errors: [
            {
                value: errorNode.errors[0].value,
                localText: errorNode.errors[0].localText,
                globalText: errorNode.errors[0].globalText,
                id: errorNode.errors[0].id,
                parentId: errorNode.errors[0].parentId,
            },
        ],
    };
};

// eslint-disable-next-line arrow-body-style
const getEmptyGlobalErrorNode = () => {
    return {
        element: 'x-error-container',
        attributes: {},
        children: [],
        errors: [],
    };
};

/**
 * Get all in-line errors
 *  @param {*} node The full manifest.
 *  @returns {*} array of error elements.
 */
export const getInlineErrors = function(node) {
    return getNodesByElementType(node, 'x-error', 'x-error-list');
};

/**
 * Copy in-line errors to route error list
 *  @param {*} routeNode The route node.
 *  @returns {void}.
 */
export const updateErrorList = function(routeNode) {
    let inlineErrors = getInlineErrors(routeNode);
    let errorNodeArray = getNodesByElementType(routeNode, 'x-error-list');
    let errorNode;
    if (errorNodeArray && Array.isArray(errorNodeArray) && errorNodeArray.length > 0) {
        errorNode = errorNodeArray[0];
        errorNode.children = errorNode.children.filter(() => false);
    } else {
        return;
    }

    inlineErrors.forEach((e) => {
        let globalError = getErrorNodeFromExistingNode(e, true);
        errorNode.children.push(globalError);
    });
};

/**
 * Copy in-line errors to global error container
 *  @param {*} manifest The full manifest.
 *  @returns {void}.
 */
export const updateGlobalError = function(manifest) {
    let inlineErrors = getInlineErrors(manifest);
    let errorNodeArray = getNodesByElementType(manifest, 'x-error-container');
    let errorNode;
    let existing = false;
    if (errorNodeArray && Array.isArray(errorNodeArray) && errorNodeArray.length > 0) {
        errorNode = errorNodeArray[0];
        if (errorNode.errors) {
            errorNode.errors = errorNode.errors.filter((e) => !e.id);
        } else {
            errorNode.errors = [];
        }
        existing = true;
    } else {
        errorNode = getEmptyGlobalErrorNode();
    }

    inlineErrors.forEach((e) => {
        let error = {};
        error.text = e.errors[0].id;
        error.id = e.errors[0].id;
        errorNode.errors.push(error);
    });

    if (!existing) {
        manifest.children.unshift(errorNode);
    }
};

/**
 * get errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @returns {void}.
 */
export const populateAllRouteErrorLists = function(manifest) {
    let routeNodes = getNodesByElementType(manifest, 'x-route');
    routeNodes.forEach((routeNode) => {
        updateErrorList(routeNode);
    });
};

/**
 * get errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @param {string} route The route to fill error list of.
 *  @returns {void}.
 */
export const populateRouteErrorList = function(manifest, route) {
    // eslint-disable-next-line no-use-before-define
    let routeNode = getRouteNode(manifest, route);
    updateErrorList(routeNode);
};

/**
 * set errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @param {string} route The route to get node for.
 *  @returns {*} the route node.
 */
export const getRouteNode = function(manifest, route) {
    let routeNodes = getNodesByElementType(manifest, 'x-route');
    return routeNodes.find((n) => n.attributes.route === route);
};

/**
 * get errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @param {string} controlId The controlId on the route to populate error list for.
 *  @returns {void}.
 */
export const populateRouteErrorListFromControlId = function(manifest, controlId) {
    // eslint-disable-next-line no-use-before-define
    let route = getRouteFromControlId(manifest, controlId);
    populateRouteErrorList(manifest, route);
};


/**
 * get errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @param {string} route The route to check.
 *  @returns {void}.
 */
export const getErrorsOnRoute = function(manifest, route) {
    let routeNode = getRouteNode(manifest, route);
    let errorListNodes = getNodesByElementType(routeNode, 'x-error-list');
    if (errorListNodes && errorListNodes.length > 0) {
        return errorListNodes[0].children;
    }

    return [];
};

/**
 * Whether there are errors on a route.
 *  @param {*} manifest The full manifest.
 *  @param {string} route The route to check.
 *  @returns {Boolean} Whether there are errors on a route.
 */
export const routeHasErrors = function(manifest, route) {
    return getErrorsOnRoute(manifest, route).length > 0;
};

/**
 * get errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @returns {void}.
 */
export const getRouteForFirstError = function(manifest) {
    let routeNodes = getNodesByElementType(manifest, 'x-route');
    let routesWithErrors = [];
    routeNodes.forEach((routeNode) => {
        if (routeHasErrors(manifest, routeNode.attributes.route)) {
            routesWithErrors.push(routeNode.attributes.route);
        }
    });

    return routesWithErrors[0];
};

/**
 * set errors on elements of a specified route.
 *  @param {*} manifest The full manifest.
 *  @param {string} controlId The control that is on the route to check.
 *  @returns {void}.
 */
export const getRouteFromControlId = function(manifest, controlId) {
    let routes = getNodesByElementType(manifest, 'x-route');
    for (let idx = 0; idx < routes.length; idx++) {
        let node = getNodeByIdRecursive(routes[idx], controlId);
        if (node) {
            return routes[idx].attributes.route;
        }
    }

    return null;
};

/**
 * Gets the global error container node.
 *  @param {*} manifest The full manifest.
 *  @returns {*} node.
 */
export const getGlobalErrorContainer = function(manifest) {
    let errorContainerNode = getNodesByElementType(manifest, 'x-error-container');
    if (errorContainerNode.length === 0) {
        return null;
    }

    return errorContainerNode[0];
};

/**
 * Determine whether form can be submitted
 *  @param {*} manifest The full manifest.
 *  @returns {boolean} whether form can be submitted.
 */
export const canSubmit = function(manifest) {
    let errorContainerNodes = getNodesByElementType(manifest, 'x-error-container');
    if (errorContainerNodes.length === 0) {
        return true;
    }

    if (errorContainerNodes.length === 1
        && errorContainerNodes[0].errors
        && errorContainerNodes[0].errors.length > 0
    ) {
        return false;
    }

    return true;
};

/**
 * Add in-line error
 *  @param {*} node The manifest node to add the error to.
 *  @param {object} errorMessageObject The error message object.
 *  @param {string} childId The id of the child item in the aggregate input.
 *  @returns {void}.
 */
export const updateNodeError = function(node, errorMessageObject, childId) {
    if (!node) {
        return;
    }

    let controlId = childId ? childId : node.attributes.id;

    let parentId = childId ? node.attributes.id : '';

    let parentNode = childId ? node : null;

    let errorObject = getErrorNode(errorMessageObject, controlId, parentId, parentNode);

    node.children = childId
        ? node.children.filter((elem) =>
            elem.errors[0].id !== childId)
        : node.children.filter(() => false);

    if (errorMessageObject) {
        node.children.push(errorObject);
    }
};

// TODO- Find place to add this testing data outside of the framework.
// This placement is just temporary.
export const getValidValueObject = function(inputType, options) {
    switch (inputType) {
        case 'x-radios': {
            return [
                {
                    value: options[0].value,
                },
            ];
        }
        case 'x-select': {
            return [
                {
                    value: options[0].value,
                },
            ];
        }
        case 'x-checkbox-list': {
            return [
                {
                    value: options[0].value,
                },
                {
                    value: options[1].value,
                },
            ];
        }
        case 'x-input-text':
        case 'x-input-text-area': {
            return [
                {
                    value: 'Test Text',
                },
            ];
        }
        case 'x-input-number': {
            return [
                {
                    value: '10',
                },
            ];
        }
        case 'x-input-email': {
            return [
                {
                    value: 'jack@gmail.com',
                },
            ];
        }
        case 'x-input-tel': {
            return [
                {
                    value: '999-999-5999',
                },
            ];
        }
        case 'x-input-decimal': {
            return [
                {
                    value: '150.2255',
                },
            ];
        }
        case 'x-input-memorable-date':
        case 'x-input-date': {
            return [
                {
                    value: '10/1/2012',
                },
            ];
        }
        case 'x-input-currency': {
            return [
                {
                    value: '100.00',
                },
            ];
        }
        case 'x-checkbox': {
            return [
                {
                    value: true,
                },
            ];
        }
        case 'x-input-name': {
            return [
                {
                    firstName: 'fname',
                    lastName: 'lname',
                },
            ];
        }
        case 'x-input-phone': {
            return [
                {
                    phone: '3334445555',
                    ext: '',
                },
            ];
        }
        case 'x-input-address': {
            return [
                {
                    address1: '666 stable mable lane',
                    address2: '',
                    city: 'texarkana',
                    state: 'WA',
                    zipCode: '98531',
                },
            ];
        }
        case 'x-payrate': {
            return [
                {
                    rate: '10.00',
                    payTimePeriod: '266',
                },
            ];
        }
        case 'x-type-ahead': {
            return [
                {
                    value: 'Aberdeen',
                },
            ];
        }
        default: return null;
    }
};



// WEBPACK FOOTER //
// ./source/framework/instance/manifest.js